import { farmavel } from '../../repo/farmavel.js'

export const login = async (username, password) => {
    try {
        const response = await farmavel.post('v1/auth/login', {
            username,
            password,
        })
        return response
    } catch (error) {
        console.error(error)
        // FIC-07: farmacia Suspendida o de Baja — credenciales válidas, acceso cerrado.
        if (error?.status === 403 && error?.data?.error === 'pharmacy_access_blocked') {
            return { accessBlocked: true }
        }
        return null
    }
}

export const getPharmDetails = async (lng = 'es') => {
    try {
        const response = await farmavel.get('v1/pharmacy/details', {
            lng,
        })
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getMonthDates = async (month, year) => {
    try {
        const response = await farmavel.get('v1/calendar/month-dates', {
            month,
            year,
        })
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getDayDates = async (date) => {
    try {
        const response = await farmavel.get('v1/calendar/day-dates', {
            date,
        })
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getCepocCandidates = async ({ patient_name = '', date_from = '', date_to = '' } = {}) => {
    try {
        const params = {}
        if (patient_name) params.patient_name = patient_name
        if (date_from) params.date_from = date_from
        if (date_to) params.date_to = date_to
        const response = await farmavel.get('v1/screenings/cepoc-candidates', params)
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const deleteCepocCandidate = async (id) => {
    try {
        const response = await farmavel.delete(`v1/screenings/cepoc-candidates/${id}`)
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getDosingResults = async ({ from = '', to = '', drug = '', patient = '', state = 'active' } = {}) => {
    try {
        const params = { status: state }
        if (from) params.filter_dosing_from = from
        if (to) params.filter_dosing_to = to
        if (drug) params.filter_dosing_drug = drug
        if (patient) params.filter_dosing_patient = patient
        const response = await farmavel.get('v1/reports/dosing-results', params)
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const toggleDosingAcceptance = async (id) => {
    try {
        const response = await farmavel.patch(`v1/reports/dosing-results/${id}/toggle-acceptance`)
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const deleteDosingResult = async (id, deletion_reason = '') => {
    try {
        const response = await farmavel.delete(`v1/reports/dosing-results/${id}`, {
            deletion_reason,
        })
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getReports = async ({
    service = '0',
    from = '',
    to = '',
    patient = '',
} = {}) => {
    try {
        const params = { filter_service_type: service }
        if (from) params.filter_service_from = from
        if (to) params.filter_service_to = to
        if (patient) params.filter_service_patient = patient
        const response = await farmavel.get('v1/reports', params)
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getSatiReport = async (pharm_id, from, to, lng = 'es') => {
    try {
        const response = await farmavel.get('v1/reports/sati', {
            user_pharm: pharm_id,
            filter_stats_from: from,
            filter_stats_to: to,
            lng,
        })
        console.log({ sati_report: response })
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}

/*
 * Carrito de reposición (FA-01 a FA-04).
 *
 * Endpoints aditivos del backend nuevo. Los importes llegan ya calculados desde
 * el servidor: aquí NO se recalculan totales ni IVA.
 *
 * A diferencia del resto del SDK, estas funciones NO devuelven `null` al fallar:
 * el carrito necesita distinguir un 409 (alguien lo cambió) de un 422
 * (validación) para poder decírselo a la farmacia. Devuelven siempre
 * `{ status, message, data }`.
 */

const cartError = (error) => ({
    status: false,
    message: error?.message || 'No se ha podido completar la operación.',
    http_status: error?.status || 0,
    data: null,
})

export const getCart = async () => {
    try {
        return await farmavel.get('v1/client/cart')
    } catch (error) {
        console.error(error)
        return cartError(error)
    }
}

/**
 * FA-06 — catálogo de productos de Farmaval que esta farmacia puede reponer.
 *
 * La visibilidad por grupo comercial la resuelve el backend: aquí no se filtra
 * nada.
 */
export const getCartCatalog = async () => {
    try {
        return await farmavel.get('v1/client/cart/catalog')
    } catch (error) {
        console.error(error)
        return cartError(error)
    }
}

/**
 * FA-06 — añade a mano un producto al carrito, aunque no se haya vendido en
 * ningún servicio. Abre el carrito si la farmacia todavía no tenía ninguno.
 */
export const addCartLine = async (productId, quantity) => {
    try {
        return await farmavel.post('v1/client/cart/lines', {
            product_id: productId,
            quantity,
        })
    } catch (error) {
        console.error(error)
        return cartError(error)
    }
}

export const updateCartLine = async (lineId, quantity) => {
    try {
        return await farmavel.patch(`v1/client/cart/lines/${lineId}`, { quantity })
    } catch (error) {
        console.error(error)
        return cartError(error)
    }
}

export const removeCartLine = async (lineId) => {
    try {
        return await farmavel.delete(`v1/client/cart/lines/${lineId}`)
    } catch (error) {
        console.error(error)
        return cartError(error)
    }
}

/**
 * Tramita el carrito. `version` viaja para que el servidor rechace el envío si
 * el carrito cambió mientras la farmacia lo revisaba (responde 409).
 */
export const submitCart = async (version) => {
    try {
        return await farmavel.post('v1/client/cart/submit', { version })
    } catch (error) {
        console.error(error)
        return cartError(error)
    }
}

export const getCartOrders = async () => {
    try {
        return await farmavel.get('v1/client/orders')
    } catch (error) {
        console.error(error)
        return cartError(error)
    }
}

/*
 * Informes: edición de comentarios y ventas (migración de `updateReport`).
 *
 * Sustituye la llamada al backend legado `farmaval-server-v2`. La respuesta
 * conserva el shape legado (`status`, `comments`, `product`) para que
 * `post_update_report()` siga funcionando sin cambios.
 */
export const updateReport = async (reportId, comments, sales = []) => {
    try {
        return await farmavel.patch(`v1/client/reports/${reportId}`, {
            rep_comments: comments ?? '',
            rep_sales: sales,
        })
    } catch (error) {
        console.error(error)
        return {
            status: false,
            message:
                error?.message ||
                'No se ha podido guardar el informe. Vuelve a intentarlo.',
        }
    }
}

export const getReportSales = async (reportId) => {
    try {
        return await farmavel.get(`v1/client/reports/${reportId}/sales`)
    } catch (error) {
        console.error(error)
        return null
    }
}

/*
 * AD-31 — productos de la farmacia. Una sola fuente en farmavel para «Mis
 * productos», el desplegable de ventas del informe y el selector de SATI/IRETIA
 * (iframe de farmaval-v1). Sustituye a `addProduct`/`deleteProduct` de
 * `farmaval-server-v2`.
 *
 * Las respuestas de éxito traen `data.products` (la lista de «Mis productos»
 * ya actualizada); los errores se devuelven con la forma `{ status, error }`
 * que pinta el formulario.
 */
const productError = (error, fallback) => ({
    status: false,
    error: error?.data?.message || error?.message || fallback,
})

/**
 * @param {string|null} service Código del servicio (SATI, IRETIA…); sin él, «Mis productos».
 */
export const getPharmacyProducts = async (service = null, lng = null) => {
    try {
        return await farmavel.get('v1/client/products', {
            ...(service ? { service } : {}),
            ...(lng ? { lng } : {}),
        })
    } catch (error) {
        console.error(error)
        return productError(error, 'No se han podido cargar los productos.')
    }
}

/**
 * Alta o edición desde «Mis productos». De un producto de Farmaval solo se
 * guarda el PVP de la farmacia; el resto de campos son del catálogo.
 */
export const savePharmacyProduct = async (product) => {
    const id = parseInt(product.prod_id, 10) || 0
    // Del catálogo llega `prod_pharm` vacío, no '0': `fill_list` no pinta los
    // valores falsy y la fila se queda sin él. Sin farmacia dueña = catálogo.
    const isCatalog = !(parseInt(product.prod_pharm, 10) > 0)

    try {
        if (id && isCatalog) {
            return await farmavel.put(`v1/client/products/${id}/price`, {
                prod_cost: product.prod_cost,
                lng: product.lng,
            })
        }

        const payload = {
            prod_name: product.prod_name,
            prod_name_ca: product.prod_name_ca,
            prod_info: product.prod_info,
            prod_info_ca: product.prod_info_ca,
            prod_cost: product.prod_cost,
            prod_state: product.prod_state,
            lng: product.lng,
        }

        return id
            ? await farmavel.put(`v1/client/products/${id}`, payload)
            : await farmavel.post('v1/client/products', payload)
    } catch (error) {
        console.error(error)
        return productError(error, 'No se ha podido guardar el producto.')
    }
}

export const deletePharmacyProduct = async (id, lng = null) => {
    try {
        return await farmavel.delete(`v1/client/products/${id}`, lng ? { lng } : null)
    } catch (error) {
        console.error(error)
        return productError(error, 'No se ha podido eliminar el producto.')
    }
}


// ── M10 — Biblioteca de Formación (endpoints aditivos /v1/training) ─────────

export const getTrainingLibrary = async () => {
    try {
        return await farmavel.get('v1/training/library')
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getTrainingFolder = async (folderId) => {
    try {
        return await farmavel.get(`v1/training/folders/${folderId}`)
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getTrainingStaff = async () => {
    try {
        return await farmavel.get('v1/training/staff')
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getTrainingPlayUrl = async (fileId, staffId) => {
    try {
        return await farmavel.get(`v1/training/files/${fileId}/play`, {
            staff_id: staffId,
        })
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getTrainingDownloadUrl = async (fileId, staffId) => {
    try {
        return await farmavel.get(`v1/training/files/${fileId}/download`, {
            staff_id: staffId,
        })
    } catch (error) {
        console.error(error)
        return null
    }
}

export const postTrainingProgress = async (payload) => {
    try {
        return await farmavel.post('v1/training/progress', payload)
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getTrainingSummary = async (staffId) => {
    try {
        return await farmavel.get('v1/training/progress/summary', {
            staff_id: staffId,
        })
    } catch (error) {
        console.error(error)
        return null
    }
}


export const getTrainingQuiz = async (folderId, staffId) => {
    try {
        return await farmavel.get(`v1/training/folders/${folderId}/quiz`, {
            staff_id: staffId,
        })
    } catch (error) {
        console.error(error)
        return null
    }
}

export const postTrainingQuizAttempt = async (quizId, payload) => {
    try {
        return await farmavel.post(`v1/training/quizzes/${quizId}/attempts`, payload)
    } catch (error) {
        console.error(error)
        return null
    }
}


export const getTrainingAwards = async (staffId) => {
    try {
        return await farmavel.get('v1/training/awards', { staff_id: staffId })
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getTrainingAwardDownload = async (staffId) => {
    try {
        return await farmavel.get('v1/training/awards/download', {
            staff_id: staffId,
        })
    } catch (error) {
        console.error(error)
        return null
    }
}


// BIB-05 — farmacia espejo del mentor (misma credencial del SuperAdmin)
export const mentorLogin = async (username, password) => {
    try {
        return await farmavel.post('v1/auth/mentor-login', { username, password })
    } catch (error) {
        console.error(error)
        return null
    }
}

// Acceso del mentor a sus farmacias: con su credencial del panel lista las farmacias
// asignadas y entra como una de ellas con un token de la propia farmacia.
export const getMentorPharmacies = async (username, password) => {
    try {
        return await farmavel.post('v1/auth/mentor/pharmacies', { username, password })
    } catch (error) {
        console.error(error)
        return { status: false, httpStatus: error?.status ?? 0 }
    }
}

export const mentorAccess = async (username, password, pharmId) => {
    try {
        return await farmavel.post('v1/auth/mentor/access', {
            username,
            password,
            pharm_id: pharmId,
        })
    } catch (error) {
        console.error(error)
        return { status: false, httpStatus: error?.status ?? 0 }
    }
}

// Cambio de farmacia con el token de mentor de la farmacia actual (se revoca).
export const mentorSwitch = async (pharmId) => {
    try {
        return await farmavel.post('v1/auth/mentor/switch', { pharm_id: pharmId })
    } catch (error) {
        console.error(error)
        return { status: false, httpStatus: error?.status ?? 0 }
    }
}


// BIB-07 — «Agendar mentoría tras la formación»
export const postMentoringRequest = async (payload) => {
    try {
        return await farmavel.post('v1/training/mentoring-requests', payload)
    } catch (error) {
        console.error(error)
        return null
    }
}


// TL-01 — formación de talleres (acceso vitalicio)
export const getTrainingWorkshops = async () => {
    try {
        return await farmavel.get('v1/training/workshops')
    } catch (error) {
        console.error(error)
        return null
    }
}

export const getTrainingWorkshop = async (serviceCode) => {
    try {
        return await farmavel.get(`v1/training/workshops/${serviceCode}`)
    } catch (error) {
        console.error(error)
        return null
    }
}

// ── Probiomentor y material divulgativo (integrados desde develop) ───────────

// ── Probiomentor — Ejecución del servicio (Etapa 08) ─────────────────────────

export const startProbiomentorService = async (staffId) => {
    try {
        return await farmavel.post('v1/probiomentor/start', { staff_id: staffId })
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getProbiomentorIndications = async () => {
    try {
        return await farmavel.get('v1/probiomentor/indications')
    } catch (error) {
        console.error(error)
        return null
    }
}
export const endProbiomentorService = async (data) => {
    try {
        return await farmavel.post('v1/probiomentor/end', data)
    } catch (error) {
        console.error(error)
        return null
    }
}
// ── Catálogo de servicios — grupos médicos ────────────────────────────────────

export const getServicesCatalog = async () => {
    try {
        return await farmavel.get('v1/services')
    } catch (error) {
        console.error('[farmavel] getServicesCatalog:', error)
        return null
    }
}
// ── Probiomentor — Consultas (Etapa 09) ───────────────────────────────────────

export const getProbiomentorHistory = async (params = {}) => {
    try {
        return await farmavel.get('v1/probiomentor/history', params)
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getProbiomentorTeamStats = async (params = {}) => {
    try {
        return await farmavel.get('v1/probiomentor/team-stats', params)
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getProbiomentorServicesStats = async (params = {}) => {
    try {
        return await farmavel.get('v1/probiomentor/services-stats', params)
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getProbiomentorConsultationsStats = async (params = {}) => {
    try {
        return await farmavel.get('v1/probiomentor/consultations-stats', params)
    } catch (error) {
        console.error(error)
        return null
    }
}
// Baja lógica (soft delete) de un servicio Probiomentor del historial.
// El backend marca `deleted_at` y lo excluye de listados y estadísticas.
export const deleteProbiomentorService = async (id, deletion_reason = '') => {
    try {
        return await farmavel.delete(`v1/probiomentor/history/${id}`, {
            deletion_reason,
        })
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getPharmProbiotics = async () => {
    try {
        return await farmavel.get('v1/pharm-probiotics')
    } catch (error) {
        console.error(error)
        return null
    }
}
export const addPharmProbiotic = async (data) => {
    try {
        return await farmavel.post('v1/pharm-probiotics', data)
    } catch (error) {
        console.error(error)
        return null
    }
}
export const updatePharmProbiotic = async (id, data) => {
    try {
        return await farmavel.patch(`v1/pharm-probiotics/${id}`, data)
    } catch (error) {
        console.error(error)
        return null
    }
}
export const removePharmProbiotic = async (id) => {
    try {
        return await farmavel.delete(`v1/pharm-probiotics/${id}`)
    } catch (error) {
        console.error(error)
        return null
    }
}
// Busca en la matriz maestra por código nacional o nombre (coincidencia parcial).
export const searchPharmProbiotics = async (q) => {
    try {
        return await farmavel.get('v1/pharm-probiotics/search', { q })
    } catch (error) {
        console.error(error)
        return null
    }
}
// Probióticos de farmacia — Etapa 05
export const lookupPharmProbiotic = async (cn) => {
    try {
        return await farmavel.get('v1/pharm-probiotics/lookup', { cn })
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getFormationLink = async () => {
    try {
        return await farmavel.get('v1/auth/formation-link')
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getMaterials = async () => {
    try {
        const response = await farmavel.get('v1/materials')
        return response
    } catch (error) {
        console.error(error)
        return null
    }
}
export const refreshPharmData = async () => {
    try {
        return await farmavel.get('v1/auth/me')
    } catch (error) {
        console.error(error)
        return { status: false, httpStatus: error?.status ?? 0 }
    }
}

// FA-07 / FA-08 — seguimiento de pacientes respiratorios (solo lectura).
export const getPatientFollowup = async (filters = {}) => {
    try {
        return await farmavel.get('v1/client/patients/followup', filters)
    } catch (error) {
        console.error(error)
        return null
    }
}
export const getPatientHistory = async (patientId) => {
    try {
        return await farmavel.get(`v1/client/patients/${patientId}/history`)
    } catch (error) {
        console.error(error)
        return null
    }
}

export { farmavel }
